import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";

import { Template } from "features/Influencer/ContactList/schema";

import Chip from "./Chip";
import Followups from "./Followups";
import ToneChips from "./ToneChips";
import {
  BRIEF_TONE_OPTIONS,
  EMPHASIS_OPTIONS,
  PersonalizationBrief,
} from "./constants";
import styles from "./styles";

interface Props {
  brief: PersonalizationBrief;
  onChange: (brief: PersonalizationBrief) => void;
  outreachTemplates: Template[];
  brandCount: number;
  creditsNeeded: number;
  alreadyGenerated: number;
  followups: Template[];
  onFollowupsChange: (followups: Template[]) => void;
}

export default function AiBrief({
  brief,
  onChange,
  outreachTemplates,
  brandCount,
  creditsNeeded,
  alreadyGenerated,
  followups,
  onFollowupsChange,
}: Props) {
  const toggleEmphasis = (value: string) =>
    onChange({
      ...brief,
      emphasis: brief.emphasis.includes(value)
        ? brief.emphasis.filter((v) => v !== value)
        : [...brief.emphasis, value],
    });

  return (
    <Box>
      <Box sx={styles.section}>
        <Typography component="label" sx={styles.fieldLabel}>
          Starting point{" "}
          <Box component="span" sx={styles.mutedNote}>
            — AI builds on this base for every brand
          </Box>
        </Typography>
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={brief.startingTemplateId ?? ""}
          onChange={(e) =>
            onChange({
              ...brief,
              startingTemplateId: e.target.value
                ? Number(e.target.value)
                : null,
            })
          }
          sx={{ borderRadius: "12px" }}
        >
          <MenuItem value="" disabled>
            Select a template
          </MenuItem>
          {outreachTemplates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Followups followups={followups} onChange={onFollowupsChange} />

      <Box sx={styles.section}>
        <Typography component="label" sx={styles.fieldLabel}>
          What should AI emphasize?
        </Typography>
        <Box sx={styles.chipRow}>
          {EMPHASIS_OPTIONS.map((option) => (
            <Chip
              key={option}
              selected={brief.emphasis.includes(option)}
              onClick={() => toggleEmphasis(option)}
            >
              {brief.emphasis.includes(option) && (
                <Box component="i" className="fa-solid fa-check" />
              )}{" "}
              {option}
            </Chip>
          ))}
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography component="label" sx={styles.fieldLabel}>
          Tone
        </Typography>
        <ToneChips
          options={BRIEF_TONE_OPTIONS}
          value={brief.tone}
          onChange={(tone) => onChange({ ...brief, tone })}
        />
      </Box>

      <Box sx={styles.section}>
        <Typography component="label" sx={styles.fieldLabel}>
          Anything specific to mention?{" "}
          <Box component="span" sx={styles.mutedNote}>
            (optional)
          </Box>
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={brief.notes ?? ""}
          onChange={(e) => onChange({ ...brief, notes: e.target.value })}
          placeholder="e.g. Open to gifting + paid. Can deliver a reel and 3 stories within a week."
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />
      </Box>

      <Box sx={styles.aiSources}>
        <Box sx={styles.aiSourcesIcon}>
          <Box component="i" className="fa-solid fa-sparkles" />
        </Box>
        <Box>
          <Typography sx={styles.infoTitle}>
            Personalized from real data
          </Typography>
          <Typography sx={styles.infoBody}>
            For each of the {brandCount} brands, Bento pulls their description
            and recent launches, then weaves in your profile. You&apos;ll review
            and approve every draft on the next step.
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.creditCost}>
        <Box sx={styles.creditCostIcon}>
          <Box component="i" className="fa-solid fa-coins" />
        </Box>
        <Box>
          <Typography sx={styles.infoTitle}>
            Uses {creditsNeeded} credit{creditsNeeded === 1 ? "" : "s"}
          </Typography>
          <Typography sx={styles.costSub}>
            {alreadyGenerated > 0
              ? `1 credit per new pitch · ${creditsNeeded} new, ${alreadyGenerated} already generated`
              : `1 credit per personalized pitch · ${brandCount} brands`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
